import React, { useCallback, useMemo, useState } from "react";
import VStack from "../../ui_atoms/VStack";
import { FeeType, MissionFee, Repetition, RepetitionType } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import ActivableListItem, { FeeData } from "../../ui_atoms/ActivableListItem";
import { BodyMediumText } from "../FormattedText";
import { useRecoilValue } from "recoil";
import { currentClientMissionFeesRate } from "../../contexts/CreateMissionManager";
import FullWidthLine from "../FullWidthLine";
import Gap from "../../ui_atoms/Gap";
import ListItem from "../../ui_atoms/ListItem";
import { useTheme } from "../../hooks/use-theme";
import { v4 as uuidv4 } from "uuid";
import { useDeepCompareEffect } from "../../hooks/use-deep-compare-effect";
import { isEqual } from "lodash";

export const missionFees = [
    {
        feeType: FeeType.PrivateTransport,
        repetitionsOptions: [
            RepetitionType.WorkedMonth,
            RepetitionType.WorkedDay,
            RepetitionType.Count,
        ],
        defaultRepetition: RepetitionType.WorkedDay,
    },
    {
        feeType: FeeType.PublicTransport,
        repetitionsOptions: [
            RepetitionType.WorkedMonth,
            RepetitionType.WorkedDay,
            RepetitionType.Count,
        ],
        defaultRepetition: RepetitionType.WorkedDay,
    },
    {
        feeType: FeeType.Food,
        repetitionsOptions: [
            RepetitionType.WorkedMonth,
            RepetitionType.WorkedDay,
            RepetitionType.Count,
        ],
        defaultRepetition: RepetitionType.WorkedDay,
    },
];

export type MissionFeeResult = Partial<MissionFee>;

type MissionFeesProps = {
    setFees: (fees: Array<MissionFeeResult>) => void;
    fees: Array<MissionFee>;
    repetition?: Repetition;
};

export const validateFeeData = (data: MissionFee) => {
    if (data.type === FeeType.Other && !data.otherTitle) {
        // if the fee is of type other, we need to validate that the title is not empty
        return false;
    }
    // if the fee is not of type other, and it is enabled, we need to validate that the
    // amount is greater than 0
    return (data.amount || 0) > 0;
};

const MissionFees: React.FC<MissionFeesProps> = ({ setFees, fees, repetition }) => {
    const i18n = useI18n();
    const clientMissionFeesRate = useRecoilValue(currentClientMissionFeesRate);
    const [cachedFees, setCachedFees] = useState<MissionFeeResult[]>([]);

    const { marginSize, colors } = useTheme();

    const [isOtherFeesEnabled, setIsOtherFeesEnabled] = useState(
        fees.some((missionFee) => missionFee.type === FeeType.Other),
    );

    const byTypeValue = useMemo(() => {
        const defaultValues = missionFees.reduce(
            (result, missionFee) => ({
                ...result,
                [missionFee.feeType]: {
                    type: missionFee.feeType,
                    isEnabled: false,
                    repetition: undefined,
                },
            }),
            {} as Record<FeeType, FeeData>,
        );
        for (const fee of fees || []) {
            if (fee && fee.type && fee.type !== FeeType.Cct) {
                defaultValues[fee.type] = {
                    type: fee.type,
                    amount: fee.amount !== undefined ? fee.amount / 100 : undefined,
                    repetition: fee.repetition || RepetitionType.WorkedDay,
                    otherTitle: fee.otherTitle,
                    externalId: fee.externalId || undefined,
                    isEnabled: true,
                };
            }
        }
        return defaultValues;
    }, [fees]);

    const onChangeFee = useCallback(
        (feeData: Partial<FeeData>) => {
            let feesResult = missionFees.reduce<Array<MissionFeeResult>>(
                (result, missionFee) => {
                    if (missionFee.feeType === feeData.type) {
                        if (feeData.isEnabled) {
                            result.push({
                                type: missionFee.feeType,
                                amount: feeData.amount ? (feeData.amount || 0) * 100 : undefined,
                                invoiceAmount: feeData.amount
                                    ? Math.trunc(
                                          (feeData.amount || 0) *
                                              (100 + Number(clientMissionFeesRate)),
                                      )
                                    : undefined,
                                repetition: feeData.repetition,
                                otherTitle: feeData.otherTitle || undefined,
                                externalId: feeData.externalId || undefined,
                            });
                        }
                    } else {
                        const currentValue = byTypeValue[missionFee.feeType];
                        if (currentValue.isEnabled) {
                            result.push({
                                type: currentValue.type,
                                amount: (currentValue.amount || 0) * 100,
                                invoiceAmount: Math.trunc(
                                    (currentValue.amount || 0) *
                                        (100 + Number(clientMissionFeesRate)),
                                ),
                                repetition: currentValue.repetition,
                                otherTitle: currentValue.otherTitle || undefined,
                                externalId: currentValue.externalId || undefined,
                            });
                        }
                    }
                    return result;
                },
                cachedFees.filter((f) => f.type === FeeType.Other),
            );

            if (feeData.type === FeeType.Other) {
                const currentOtherFeeIndex = feesResult.findIndex((f) => f.id === feeData.id);

                if (currentOtherFeeIndex !== -1) {
                    if (feeData.isEnabled) {
                        const { isEnabled: _, ...restFeeData } = feeData;
                        feesResult[currentOtherFeeIndex] = {
                            ...cachedFees[currentOtherFeeIndex],
                            ...restFeeData,
                            amount: feeData.amount ? (feeData.amount || 0) * 100 : undefined,
                            invoiceAmount: feeData.amount
                                ? Math.trunc(
                                      (feeData.amount || 0) * (100 + Number(clientMissionFeesRate)),
                                  )
                                : undefined,
                        };
                    } else {
                        feesResult = feesResult.filter((f) => f.id !== feeData.id);
                    }
                } else {
                    if (feeData.isEnabled) {
                        const { isEnabled: _isEnabled, ...restFeeData } = feeData;
                        feesResult.push({
                            ...restFeeData,
                            amount: feeData.amount ? (feeData.amount || 0) * 100 : undefined,
                            invoiceAmount: feeData.amount
                                ? Math.trunc(
                                      (feeData.amount || 0) * (100 + Number(clientMissionFeesRate)),
                                  )
                                : undefined,
                        });
                    }
                }
            }

            setCachedFees(feesResult);

            const _isOtherFeesEnabled = feesResult.some(
                (missionFee) => missionFee.type === FeeType.Other,
            );

            setIsOtherFeesEnabled(_isOtherFeesEnabled);

            const filteredFees = feesResult.filter(
                (fee) => _isOtherFeesEnabled || fee.type !== FeeType.Other,
            );

            setFees(filteredFees);
        },
        [byTypeValue, cachedFees, clientMissionFeesRate, setFees],
    );

    useDeepCompareEffect(() => {
        if (!isEqual(cachedFees, fees)) {
            setIsOtherFeesEnabled(fees.some((missionFee) => missionFee.type === FeeType.Other));
        }
    }, [fees]);

    const buildRepetitionOption = useCallback(
        (repetitionOption: RepetitionType) => {
            let label = i18n.t(`fees.repetition.${repetitionOption}`);
            if (repetition === Repetition.None && repetitionOption === RepetitionType.WorkedMonth) {
                label = i18n.t("fees.repetition.total");
            }
            return {
                label,
                value: repetitionOption,
            };
        },
        [i18n, repetition],
    );

    const otherFees = useMemo(
        () => fees.filter((missionFee) => missionFee.type === FeeType.Other),
        [fees],
    );

    return (
        <VStack gap={1}>
            <BodyMediumText>
                {i18n.t("screens.user.create_mission_fees.description")}
            </BodyMediumText>
            <>
                <VStack gap="2/3">
                    <VStack gap="2/3">
                        {missionFees.map((missionFee) => {
                            const currentFee = fees.find((f) => f.type === missionFee.feeType);

                            return (
                                <>
                                    <ActivableListItem
                                        key={missionFee.feeType}
                                        title={i18n.t(`fees.type.${missionFee.feeType}.title`)}
                                        description={i18n.t(
                                            `fees.type.${missionFee.feeType}.description`,
                                        )}
                                        repetitionsOptions={missionFee.repetitionsOptions.map(
                                            buildRepetitionOption,
                                        )}
                                        value={byTypeValue[missionFee.feeType]}
                                        onChange={(changes) => {
                                            onChangeFee({
                                                ...currentFee,
                                                ...byTypeValue[missionFee.feeType],
                                                ...changes,
                                            });
                                        }}
                                    />
                                    <Gap gap="2/3" />
                                    <FullWidthLine />
                                </>
                            );
                        })}
                    </VStack>
                    <ActivableListItem
                        title={i18n.t(`fees.type.other.title`)}
                        repetitionsOptions={[
                            RepetitionType.WorkedMonth,
                            RepetitionType.WorkedDay,
                            RepetitionType.Count,
                        ].map(buildRepetitionOption)}
                        value={{ isEnabled: isOtherFeesEnabled }}
                        onChange={(changes) => {
                            const otherFeeConfig = {
                                isEnabled: true,
                                ...changes,
                            };
                            if (otherFeeConfig.isEnabled && !isOtherFeesEnabled) {
                                if (
                                    cachedFees.filter((fee) => fee.type === FeeType.Other)
                                        .length === 0
                                ) {
                                    // Add a first other fee when the Switch is turned on the first time
                                    onChangeFee({
                                        id: uuidv4(),
                                        isEnabled: true,
                                        type: FeeType.Other,
                                        repetition: RepetitionType.WorkedDay,
                                    });
                                } else {
                                    setFees(cachedFees);
                                }
                            } else if (!otherFeeConfig.isEnabled) {
                                // When disabling Other fees, save current `fees` in `cachedFees`
                                setCachedFees(fees);
                                setFees(fees.filter((fee) => fee.type !== FeeType.Other));
                            }
                            setIsOtherFeesEnabled(otherFeeConfig.isEnabled);
                        }}
                    />
                    {isOtherFeesEnabled ? (
                        otherFees.map((fee) => {
                            const otherFeeConfig = {
                                ...fee,
                                isEnabled: true,
                                type: FeeType.Other,
                                amount: fee.amount !== undefined ? fee.amount / 100 : undefined,
                                repetition: fee.repetition || RepetitionType.WorkedDay,
                            };
                            return (
                                <>
                                    <ActivableListItem
                                        key={fee.id}
                                        title={i18n.t(`fees.type.${otherFeeConfig.type}.title`)}
                                        repetitionsOptions={[
                                            RepetitionType.WorkedDay,
                                            RepetitionType.WorkedMonth,
                                            RepetitionType.Count,
                                        ].map(buildRepetitionOption)}
                                        value={otherFeeConfig}
                                        onChange={(changes) => {
                                            onChangeFee({
                                                ...otherFeeConfig,
                                                ...changes,
                                            });
                                        }}
                                    />
                                </>
                            );
                        })
                    ) : (
                        <></>
                    )}
                </VStack>
                {isOtherFeesEnabled && (
                    <ListItem
                        style={{ marginHorizontal: -marginSize }}
                        labels={[
                            {
                                text: i18n.t(`screens.user.create_mission_fees.add_other_fees`),
                                mode: "cta",
                            },
                        ]}
                        rightIcon="add"
                        rightActionMode="medium"
                        rightIconColor={colors.secondary}
                        onPress={() => {
                            onChangeFee({
                                id: uuidv4(),
                                isEnabled: true,
                                type: FeeType.Other,
                                repetition: RepetitionType.WorkedDay,
                            });
                        }}
                    />
                )}
            </>
        </VStack>
    );
};

export default MissionFees;
